<template>
  <div class="job-profile-editor-content">
    <FirstDraftProgress />
    <DpplHeaderFooterContent>
      <div
        v-show="!pageLoading"
        class="w-100 pa-6 d-flex flex-column ga-2"
      >
        <div class="d-flex">
          <JobProfileStatus
            :is-template="isTemplate"
            :is-hris-profile="isHrisProfile"
            :status="jobProfile?.status"
          />
        </div>

        <div class="text-break py-2">
          <TitleEditor
            :value="jobProfile?.title"
            :highlights="getTitleHighlights()"
            :placeholder="`Job profile title`"
            :is-read-only="readOnly"
            @update:model-value="onEditorUpdate"
          />
        </div>

        <div class="d-flex flex-wrap ga-2">
          <div title="Location">
            <InlineEditor
              :editor-id="'location'"
              :value="jobProfile?.location"
              :highlights="getLocationHighlights()"
              placeholder="Location"
              :is-read-only="readOnly"
              @update:model-value="onEditorUpdate"
            />
          </div>

          <div
            v-if="!isTemplate"
            class="d-flex"
            title="Job code"
          >
            <div class="text-caption mr-2">
              -
            </div>

            <InlineEditor
              :editor-id="'jobCode'"
              :value="jobProfile?.job_code"
              placeholder="Job code"
              :is-read-only="readOnly"
              @update:model-value="onEditorUpdate"
            />
          </div>

          <div
            v-if="isHrisProfile && jobProfile?.is_flsa_exempt"
            class="text-caption d-flex ga-2"
          >
            <div>
              -
            </div>

            <div class="text-textLight">
              Exempt
            </div>
          </div>

          <div class="text-caption">
            -
          </div>

          <v-sheet
            min-width="100px"
            :title="jobProfile?.last_applied_template_ulid
              ? 'Level is locked because it was updated from a template'
              : 'Job Level'
            "
          >
            <JobLevelEditor
              :value="jobProfile?.job_level_ulid"
              placeholder="Job level"
              :is-read-only="readOnly || Boolean(jobProfile?.last_applied_template_ulid)"
              @update:model-value="onJobLevelUpdate"
            />
          </v-sheet>
        </div>

        <div>
          <div
            v-for="field in fields"
            :key="field.id"
            class="py-2"
            @mouseover="hoveredFieldId = field.id"
            @mouseleave="hoveredFieldId = ''"
          >
            <v-sheet
              class="d-flex justify-end mt-2 ga-3 bg-bgDefault"
              :min-height="24"
            >
              <div
                v-if="!readOnly"
              >
                <FirstDraftButton
                  v-if="authData.aiEnabled && field.content_generator_type === 'summary'"
                  v-show="hoveredFieldId === field.id"
                  variant="plain"
                  :disabled="readOnly || rescorePending"
                  small
                  width="30px"
                  min-width="30px"
                  @click="onGenerateSummaryContent"
                />

                <template
                  v-for="generatorType in [
                    'responsibilities',
                    'skills',
                    'experience',
                    'competencies',
                    'education',
                    'certifications',
                  ]"
                  :key="generatorType"
                >
                  <FirstDraftButton
                    v-if="authData.aiEnabled && field.content_generator_type === generatorType"
                    v-show="hoveredFieldId === field.id"
                    variant="plain"
                    :disabled="readOnly || rescorePending"
                    small
                    width="30px"
                    min-width="30px"
                    @click="onGenerateFieldContent(field)"
                  />
                </template>

                <v-btn
                  v-if="field.is_job_description"
                  variant="outlined"
                  :disabled="readOnly || rescorePending"
                  size="small"
                  height="20"
                  color="primary"
                  width="30px"
                  min-width="30px"
                  @click="setModal({ field: 'updateProfileJobDescription', value: true })"
                >
                  <v-icon
                    size="16"
                    icon="mdi-refresh"
                  />
                </v-btn>
              </div>

              <FieldLabel>
                {{ field.label }}
              </FieldLabel>
            </v-sheet>

            <!-- v-show for header and footer to overwrite editorContents parts with actual content -->
            <div
              v-show="isTemplate || field.headerHtml?.length >= 10"
            >
              <DescriptionEditor
                :value="field.headerHtml"
                :editor-id="`${field.id}__header`"
                :is-read-only="!isTemplate || readOnlyTemplate"
                :is-locked="true"
                :disabled-nodes="field.disabledNodes"
                @update:model-value="onEditorUpdate"
              />
            </div>

            <div>
              <DescriptionEditor
                :value="field.html"
                :editor-id="field.id"
                :is-read-only="readOnly"
                :highlights="getHighlights(field.type)"
                :disabled-nodes="field.disabledNodes"
                @update:model-value="onEditorUpdate"
                @link-edit="onLinkEdit"
                @add-editor="onAddEditor"
                @blur-editor="onBlurEditor"
                @remove-editor="onRemoveEditor"
                @update-selected-json="onUpdateSelectedJson"
                @focus-editor="onFocusEditor"
              />
            </div>

            <div
              v-show="isTemplate || field.footerHtml?.length >= 10"
            >
              <DescriptionEditor
                :value="field.footerHtml"
                :editor-id="`${field.id}__footer`"
                :is-read-only="!isTemplate || readOnlyTemplate"
                :is-locked="true"
                :disabled-nodes="field.disabledNodes"
                @update:model-value="onEditorUpdate"
              />
            </div>
          </div>
        </div>
      </div>
    </DpplHeaderFooterContent>
    <UpdateJobDescriptionModal
      :show="show.updateProfileJobDescription"
      :fields="selectableFields"
      @close="setModal({ field: 'updateProfileJobDescription', value: false })"
      @action="onUpdateJobDescriptionAction"
    />
    <GenerateSummaryConfirmModal />
    <SummaryPickerModal />
    <GenerateFieldContentConfirmModal />
    <RestoreRevisionConfirmModal />
    <HrisProfileLinkModal />
    <UnlinkHrisProfileConfirmModal />
    <UpdateFromTemplateConfirmModal />
  </div>
</template>

<script setup>
import {
  ref, computed, onErrorCaptured, watch,
} from 'vue';
import { storeToRefs } from 'pinia';
import { useAppAuthData } from '@/stores/app/authData';
import { useAppLoading } from '@/stores/app/loading';
import { useAppAlerts } from '@/stores/app/alerts';
import { useDocumentEditor } from '@/stores/document/editor';
import { useDocumentEditorToolbar } from '@/stores/document/editorToolbar';
import { useCurrentJobProfile } from '@/stores/jobProfiles/currentJobProfile';
import { useContextualProfileSummary } from '@/stores/jobProfiles/contextualProfileSummary';
import { useHighlights } from '@/stores/highlights/highlights';
import { useModals } from '@/stores/modals/modals';
import { isEmptyHtml } from '@ui/helpers/htmlToText';
import DpplHeaderFooterContent from '@ui/components/general/DpplHeaderFooterContent/DpplHeaderFooterContent.vue';
import FirstDraftProgress from '@/components/firstDraft/FirstDraftProgress.vue';
import DescriptionEditor from '@ui/components/editor/DescriptionEditor/DescriptionEditor.vue';
import FieldLabel from '@/components/editor/field/FieldLabel.vue';
import InlineEditor from '@ui/components/editor/InlineEditor/InlineEditor.vue';
import TitleEditor from '@ui/components/editor/TitleEditor/TitleEditor.vue';
import FirstDraftButton from '@ui/components/firstDraft/FirstDraftButton.vue';
import JobLevelEditor from './JobLevelEditor.vue';
import JobProfileStatus from './JobProfileStatus.vue';
import UpdateJobDescriptionModal from './UpdateJobDescriptionModal.vue';
import GenerateSummaryConfirmModal from './GenerateSummaryConfirmModal.vue';
import SummaryPickerModal from './SummaryPickerModal.vue';
import GenerateFieldContentConfirmModal from './GenerateFieldContentConfirmModal.vue';
import RestoreRevisionConfirmModal from './RestoreRevisionConfirmModal.vue';
import HrisProfileLinkModal from './HrisProfileLinkModal.vue';
import UnlinkHrisProfileConfirmModal from './UnlinkHrisProfileConfirmModal.vue';
import UpdateFromTemplateConfirmModal from './UpdateFromTemplateConfirmModal.vue';

const hoveredFieldId = ref('');

const currentJobProfile = useCurrentJobProfile();
const {
  jobProfile, fields, isHrisProfile, isTemplate, rescorePending, editorContents, readOnly, readOnlyTemplate,
  showPendingApprovalAlert,
} = storeToRefs(currentJobProfile);
const { pageLoading } = storeToRefs(useAppLoading());
const { authData } = storeToRefs(useAppAuthData());
const { setModal } = useModals();
const { show } = storeToRefs(useModals());

const getHighlights = (fieldId) => useHighlights().descriptionHighlights.filter((hl) => hl.fieldId === fieldId);

const getTitleHighlights = () => useHighlights().titleHighlights;

const getLocationHighlights = () => useHighlights().locationHighlights;

const onLinkEdit = () => {
  useDocumentEditorToolbar().onLinkButtonClick();
};

const onAddEditor = ({ editorId, tiptap }) => {
  useDocumentEditor().addTiptap(editorId, tiptap);
};

const onBlurEditor = () => {
  useDocumentEditor().updateFocus('');
};

const onRemoveEditor = ({ editorId }) => {
  useDocumentEditor().removeTiptap(editorId);
};

const onUpdateSelectedJson = ({ editorId, extensions }) => {
  useDocumentEditor().updateSelectedJson({ editorId, extensions });
};

const onFocusEditor = ({ editorId }) => {
  useDocumentEditor().updateFocus(editorId);
};

const onEditorUpdate = ({ editorId, html }) => {
  const [id, part] = editorId?.split('__') || [];
  currentJobProfile.updateEditorContent({ editorId: id, part, html });
};

const onJobLevelUpdate = (ulid) => {
  currentJobProfile.updateJobLevelUlid(ulid);
};

const selectableFields = computed(() => fields.value
  .filter((f) => !f.is_job_description)
  .filter((f) => {
    const editor = editorContents.value[f.id] || {};
    const header = editor.header || f.headerHtml || '';
    const content = editor.content || f.html || '';
    const footer = editor.footer || f.footerHtml || '';
    return [header, content, footer].some((s) => !isEmptyHtml(s));
  }));

const onGenerateSummaryContent = () => {
  useContextualProfileSummary().openSummaryModal();
};

const onGenerateFieldContent = (field) => {
  setModal({
    field: 'generateFieldContentConfirm',
    value: true,
    params: { fieldTitle: field.label, generatorType: field.content_generator_type },
  });
};

const onUpdateJobDescriptionAction = ({ mode, selectedFieldIds }) => {
  setModal({ field: 'updateProfileJobDescription', value: false });
  currentJobProfile.generateProfileJobDescription(mode, selectedFieldIds);
};

onErrorCaptured((err) => {
  if (err?.message === 'Highlight text not found in the field content at all') {
    useAppAlerts().addAlert({ type: 'error', message: err.message, onlyOnceId: 'highlights' });
    return false;
  }
  if (err?.message?.startsWith('Cannot find a field for highlight')) {
    useAppAlerts().addAlert({ type: 'error', message: err.message, onlyOnceId: 'highlights2' });
    return false;
  }
  return false;
});

watch(showPendingApprovalAlert, (newValue, oldValue) => {
  if (newValue && newValue !== oldValue) {
    useAppAlerts().addAlert({
      type: 'info',
      message: 'This job profile is in an approval flow. You cannot edit it until this review is resolved.',
      onlyOnceId: 'hasPendingApprovalFlows',
      thisPageOnly: true,
    });
  }
}, { immediate: true });
</script>
